import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import { Label } from "components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";

import { CAMPAIGN_TYPE_LABELS, type IConcurrencyGroup } from "@/@types/gamification";
import { cn } from "@/lib/utils";

import {
  CONCURRENCY_GROUP_ITEM_PREFIX,
  CONCURRENCY_GROUP_TRIGGER_ID,
  ConcurrencyGroupListState,
} from "../constants/concurrency-group";
import { CONCURRENCY_GROUP_COPY } from "../constants/messages";
import { isSameGroupName, normalizeGroupName } from "../utils/concurrency-group";
import { ConcurrencyGroupComposition } from "./concurrency-group-composition";
import {
  useConcurrencyGroupCombobox,
  type UseConcurrencyGroupComboboxResult,
} from "./use-concurrency-group-combobox";

const HELP_ID = `${CONCURRENCY_GROUP_TRIGGER_ID}-help`;
const ERROR_ID = `${CONCURRENCY_GROUP_TRIGGER_ID}-error`;

interface ConcurrencyGroupFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  errorMessage?: string;
  currentCampaignId?: number;
}

interface ConcurrencyGroupPickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  errorMessage?: string;
  currentCampaignId?: number;
}

function GroupOption({
  group,
  selected,
  onSelect,
}: {
  group: IConcurrencyGroup;
  selected: boolean;
  onSelect: (name: string) => void;
}): JSX.Element {
  return (
    <CommandItem
      value={`${CONCURRENCY_GROUP_ITEM_PREFIX.GROUP}${group.name}`}
      onSelect={() => onSelect(group.name)}
      className="items-start"
    >
      <Check
        aria-hidden="true"
        className={cn("mt-0.5", selected ? "opacity-100" : "opacity-0")}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="break-words font-medium">{group.name}</p>
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="secondary" className="font-normal">
            {CONCURRENCY_GROUP_COPY.countSummary(group.campaignCount, group.activeCount)}
          </Badge>
          {group.mechanics.map((mechanic) => (
            <Badge key={mechanic} variant="outline" className="font-normal">
              {CAMPAIGN_TYPE_LABELS[mechanic]}
            </Badge>
          ))}
        </div>
      </div>
    </CommandItem>
  );
}

function GroupListStatus({
  combobox,
}: {
  combobox: UseConcurrencyGroupComboboxResult;
}): JSX.Element | null {
  if (combobox.listState === ConcurrencyGroupListState.LOADING) {
    return (
      <p role="status" className="px-3 py-4 text-sm text-muted-foreground">
        {CONCURRENCY_GROUP_COPY.loading}
      </p>
    );
  }
  if (combobox.listState === ConcurrencyGroupListState.ERROR) {
    return (
      <div role="alert" className="flex items-center justify-between gap-2 px-3 py-3 text-sm">
        <span className="text-red-600">{CONCURRENCY_GROUP_COPY.error}</span>
        <Button type="button" variant="outline" size="sm" onClick={combobox.retry}>
          {CONCURRENCY_GROUP_COPY.retry}
        </Button>
      </div>
    );
  }
  if (combobox.groups.length > 0) return null;
  return (
    <p className="px-3 py-4 text-center text-sm text-muted-foreground">
      {combobox.hasSearch
        ? CONCURRENCY_GROUP_COPY.emptyNoMatch
        : CONCURRENCY_GROUP_COPY.emptyNoGroups}
    </p>
  );
}

function ConcurrencyGroupPicker({
  value,
  onChange,
  onBlur,
  errorMessage,
  currentCampaignId,
}: ConcurrencyGroupPickerProps): JSX.Element {
  const combobox: UseConcurrencyGroupComboboxResult = useConcurrencyGroupCombobox({
    onSelect: onChange,
    excludeCampaignId: currentCampaignId,
  });
  const selectedName: string = normalizeGroupName(value);
  const describedBy: string = errorMessage ? `${HELP_ID} ${ERROR_ID}` : HELP_ID;

  const handleOpenChange = (next: boolean): void => {
    combobox.setOpen(next);
    if (!next) onBlur();
  };

  return (
    <div className="space-y-3">
      <div className="max-w-md space-y-1">
        <Label htmlFor={CONCURRENCY_GROUP_TRIGGER_ID}>{CONCURRENCY_GROUP_COPY.label}</Label>
        <Popover open={combobox.open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id={CONCURRENCY_GROUP_TRIGGER_ID}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={combobox.open}
              aria-describedby={describedBy}
              aria-invalid={Boolean(errorMessage)}
              className={cn(
                "w-full justify-between font-normal",
                !selectedName && "text-muted-foreground",
              )}
            >
              <span className="truncate">
                {selectedName || CONCURRENCY_GROUP_COPY.triggerPlaceholder}
              </span>
              <ChevronsUpDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-[280px] p-0"
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            <Command shouldFilter={false} label={CONCURRENCY_GROUP_COPY.searchLabel}>
              <CommandInput
                aria-label={CONCURRENCY_GROUP_COPY.searchLabel}
                placeholder={CONCURRENCY_GROUP_COPY.searchPlaceholder}
                value={combobox.search}
                onValueChange={combobox.setSearch}
              />
              <CommandList>
                {combobox.createName && (
                  <CommandGroup heading={CONCURRENCY_GROUP_COPY.createHeading}>
                    <CommandItem
                      value={`${CONCURRENCY_GROUP_ITEM_PREFIX.CREATE}${combobox.createName}`}
                      onSelect={() => combobox.select(combobox.createName ?? "")}
                    >
                      <Plus aria-hidden="true" />
                      <span className="break-words">
                        {CONCURRENCY_GROUP_COPY.create(combobox.createName)}
                      </span>
                    </CommandItem>
                  </CommandGroup>
                )}
                <GroupListStatus combobox={combobox} />
                {combobox.listState === ConcurrencyGroupListState.READY &&
                  combobox.groups.length > 0 && (
                    <CommandGroup heading={CONCURRENCY_GROUP_COPY.existingHeading}>
                      {combobox.groups.map((group) => (
                        <GroupOption
                          key={group.name}
                          group={group}
                          selected={isSameGroupName(group.name, selectedName)}
                          onSelect={combobox.select}
                        />
                      ))}
                    </CommandGroup>
                  )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p id={HELP_ID} className="text-xs text-muted-foreground">
          {CONCURRENCY_GROUP_COPY.help}
        </p>
        {errorMessage && (
          <p id={ERROR_ID} className="text-sm text-red-500">
            {errorMessage}
          </p>
        )}
      </div>

      {selectedName && (
        <ConcurrencyGroupComposition
          groupName={selectedName}
          excludeCampaignId={currentCampaignId}
        />
      )}
    </div>
  );
}

export function ConcurrencyGroupField<T extends FieldValues>({
  control,
  name,
  errorMessage,
  currentCampaignId,
}: ConcurrencyGroupFieldProps<T>): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <ConcurrencyGroupPicker
          value={String(field.value ?? "")}
          onChange={field.onChange}
          onBlur={field.onBlur}
          errorMessage={errorMessage}
          currentCampaignId={currentCampaignId}
        />
      )}
    />
  );
}
